import { actions as usersActions } from '@/api/users';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { getUrlSearchParam } from '@payaca/helpers/urlHelper';

import { Modal, Text } from '@payaca/ambrosia';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { PermissionGuard } from '../../components/permissionGuard/PermissionGuard';
import { CityPlumbingConnectionControl } from './cityPlumbingConnectionControl/CityPlumbingConnectionControl';
import { EasyPVConnectionControl } from './easyPVConnectionControl/EasyPVConnectionControl';
import { FornaxConnectionControl } from './fornaxConnectionControl/FornaxConnectionControl';
import { HeatpunkConnectionControl } from './heatpunkConnectionControl/HeatpunkConnectionControl';
import { OpenSolarConnectionControl } from './openSolarConnectionControl/OpenSolarConnectionControl';
import QuickbooksConnectionControl from './quickbooksConnectionControl/QuickbooksConnectionControl';
import { SpruceConnectionControl } from './spruceConnectionControl/SpruceConnectionControl';
import StripeConnectionControl from './stripeConnectionControl/StripeConnectionControl';
import XeroConnectionControl from './xeroConnectionControl/XeroConnectionControl';
import ZapierConnectionControl from './zapierConnectionControl/ZapierConnectionControl';

/**
 * The connections settings section, rendered in the company settings pane.
 * `service` is the trailing URL segment — the OAuth return target for Xero
 * and Quickbooks (e.g. .../connections/xero?code=...).
 */
export const ConnectionsSettings: FunctionComponent<{ service?: string }> = (
  props
): JSX.Element => {
  const { service } = props;
  const dispatch = useDispatch();

  const location = useLocation();
  const history = useHistory();
  const [
    showStripeConnectionSuccessModal,
    setShowStripeConnectionSuccessModal,
  ] = useState(false);

  const [isCompletingXeroConnection, setIsCompletingXeroConnection] =
    useState(false);
  const [
    isCompletingQuickbooksConnection,
    setIsCompletingQuickbooksConnection,
  ] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const code = useMemo(() => {
    return getUrlSearchParam(location.search, 'code');
  }, [location]);

  const realmId = useMemo(() => {
    return getUrlSearchParam(location.search, 'realmId');
  }, [location]);

  const isStripeConfirmationSuccessRedirect = useMemo(() => {
    return (
      getUrlSearchParam(
        location.search,
        'isStripeConnectionSuccessRedirect'
      ) === 'true'
    );
  }, [location]);

  useEffect(() => {
    if (isStripeConfirmationSuccessRedirect) {
      setShowStripeConnectionSuccessModal(true);
    }
  }, [isStripeConfirmationSuccessRedirect]);

  const connectWithXero = useCallback(() => {
    setIsCompletingXeroConnection(true);
    dispatch(
      usersActions.connectWithXero(code, (error: string) => {
        if (!error) {
          dispatch(
            usersActions.getProfile(() => {
              dispatch(
                usersActions.getXeroAccounts(() => {
                  history.push(
                    '/companySettings/integrations-data/connections'
                  );
                  setIsCompletingXeroConnection(false);
                })
              );
            })
          );
        } else {
          setErrorMessage('Unable to connect with Xero');
          setIsCompletingXeroConnection(false);
        }
      })
    );
  }, [dispatch, history, code]);

  const connectWithQuickbooks = useCallback(() => {
    setIsCompletingQuickbooksConnection(true);
    dispatch(
      usersActions.connectWithQuickbooks(
        {
          code: code,
          realmId: realmId,
        },
        (error: string) => {
          if (!error) {
            dispatch(
              usersActions.getProfile(() => {
                history.push('/companySettings/integrations-data/connections');
                setIsCompletingQuickbooksConnection(false);
              })
            );
          } else {
            setErrorMessage('Unable to connect with Quickbooks');
            setIsCompletingQuickbooksConnection(false);
          }
        }
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'realmId'
  }, [dispatch, history, code]);

  useEffect(() => {
    if (code) {
      if (service === 'xero') {
        connectWithXero();
      } else if (service === 'quickbooks') {
        connectWithQuickbooks();
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'connectWithQuickbooks', 'service', 'code', and 'connectWithXero'
  }, []);

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2">
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_SUPPLIERS,
            AccountsPermissions.UPDATE_CONNECTION_SUPPLIERS,
          ]}
        >
          <CityPlumbingConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_ACCOUNTING,
            AccountsPermissions.UPDATE_CONNECTION_ACCOUNTING,
          ]}
        >
          <QuickbooksConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_PAYMENT,
            AccountsPermissions.UPDATE_CONNECTION_PAYMENT,
          ]}
        >
          <StripeConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_PAYMENT,
            AccountsPermissions.UPDATE_CONNECTION_PAYMENT,
          ]}
        >
          <FornaxConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_ACCOUNTING,
            AccountsPermissions.UPDATE_CONNECTION_ACCOUNTING,
          ]}
        >
          <XeroConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_AUTOMATION,
            AccountsPermissions.UPDATE_CONNECTION_AUTOMATION,
          ]}
        >
          <ZapierConnectionControl />
        </PermissionGuard>
        <EasyPVConnectionControl />
        <OpenSolarConnectionControl />
        <HeatpunkConnectionControl />
        <SpruceConnectionControl />
      </div>
      <Modal
        isOpen={showStripeConnectionSuccessModal}
        title="Stripe connected"
        onClose={() => setShowStripeConnectionSuccessModal(false)}
      >
        <Modal.Body>
          <Text>
            You‘ve successfully connected your Stripe account to Payaca and can
            now accept card payments.
          </Text>
        </Modal.Body>
      </Modal>
    </>
  );
};
